// frontend/src/components/EmployeePayslipCard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Receipt,
  Download,
  Visibility,
  Info,
  CurrencyRupee,
  TrendingUp,
  TrendingDown,
  AccountBalanceWallet
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';


const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  border: '2px solid #d8b4fe',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(139, 92, 246, 0.15)',
    borderColor: '#c084fc',
  },
  height: '320px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #8a5cf688 0%, #ec489a87 100%)',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
  }
}));

const SalaryBox = styled(Box)(({ theme, highlight }) => ({
  backgroundColor: highlight ? '#e8f5e9' : '#f8f9fa',
  borderRadius: '12px',
  padding: theme.spacing(1.2, 1),
  textAlign: 'center',
  flex: 1,
  transition: 'all 0.2s ease',
  border: highlight ? '1px solid #4caf50' : '1px solid transparent',
  '&:hover': {
    backgroundColor: highlight ? '#c8e6c9' : '#f0f2f5',
    transform: 'translateY(-1px)',
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  flex: 1,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
  }
}));

const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function EmployeePayslipCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payslip, setPayslip] = useState(null);
  const [currentMonth, setCurrentMonth] = useState('');
  const [currentYear, setCurrentYear] = useState('');
  const [displayMonth, setDisplayMonth] = useState('');
  const [displayYear, setDisplayYear] = useState('');
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  const getEmployeeIdFromToken = async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.employee_id) return payload.employee_id;
      
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`,{
        withCredentials: true
      });
      return response.data.employee_id;
    } catch (error) {
      //console.error('Error getting employee ID:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchPayslipData();
  }, []);

  const getPreviousMonth = (month, year) => {
    const monthIndex = monthNames.indexOf(month);
    if (monthIndex === 0) {
      return {
        month: monthNames[11],
        year: year - 1
      };
    } else {
      return {
        month: monthNames[monthIndex - 1],
        year: year
      };
    }
  };

  const fetchPayslipData = async () => {
    setLoading(true);
    setError(null);
    try {
       const token = localStorage.getItem('token');
    if (!token) return null;
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      
      if (!employee_id || !company_id) {
        setError('Authentication error');
        return;
      }

      const now = new Date();
      const currentMonthName = monthNames[now.getMonth()];
      const currentYearNum = now.getFullYear();

      setCurrentMonth(currentMonthName);
      setCurrentYear(currentYearNum);

      let found = false;
      let searchMonth = currentMonthName;
      let searchYear = currentYearNum;
      let attempts = 0;
      const maxAttempts = 24;

      while (!found && attempts < maxAttempts) {
        try {
          const response = await axios.get(
            `${API_URL}/api/payslip/${searchMonth}/${searchYear}`,
            { withCredentials: true,
              headers:{
                Authorization:`Bearer ${token}`
              }
             }
          );
          
          if (response.data.success) {
            setPayslip(response.data.data);
            setDisplayMonth(searchMonth);
            setDisplayYear(searchYear);
            found = true;
            break;
          }
        } catch (err) {
          const previous = getPreviousMonth(searchMonth, searchYear);
          searchMonth = previous.month;
          searchYear = previous.year;
          attempts++;
        }
      }

      if (!found) {
        setError('No payslip available');
      }
    } catch (error) {
      //console.error('Error fetching payslip:', error);
      setError('Failed to load payslip data');
    } finally {
      setLoading(false);
    }
  };
  const handleDownloadPDF = async () => {
    if (!payslip) return;
    
    setDownloading(true);
    try {
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      
      // Use fetch instead of axios for better blob handling
      const response = await fetch(
        `${API_URL}/api/payslip/download/${employee_id}/${displayMonth}/${displayYear}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} ${errorText}`);
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Check if it's a PDF
      if (blob.type !== 'application/pdf') {
        console.warn('Response is not PDF, type:', blob.type);
        // Try to read as text for error message
        const text = await blob.text();
        if (text) {
          throw new Error(`Server returned: ${text.substring(0, 200)}`);
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payslip_${employee_id}_${displayMonth}_${displayYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSnackbar({ open: true, message: 'Payslip downloaded successfully', severity: 'success' });
    } catch (error) {
      //console.error('Error downloading payslip:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to download payslip', 
        severity: 'error' 
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleViewFull = () => {
    navigate('/view-payslip', { state: { month: displayMonth, year: displayYear } });
  };

  const isCurrentMonth = displayMonth === currentMonth && displayYear === currentYear;

  if (loading) {
    return (
      <StyledCard>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={36} />
        </CardContent>
      </StyledCard>
    );
  }

  if (error) {
    return (
      <StyledCard>
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Receipt sx={{ color: '#8b5cf6', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight="600" color="#1a1a1a">
              Payslip
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
            {error}
          </Alert>
          <ActionButton
            variant="outlined"
            startIcon={<Visibility sx={{ fontSize: 18 }} />}
            onClick={() => navigate('/view-payslip')}
            sx={{ borderColor: '#e0e0e0', color: '#666' }}
          >
            Go to Payslip Page
          </ActionButton>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <>
      <StyledCard>
        <CardContent sx={{ p: 2.5 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Receipt sx={{ color: '#8b5cf6', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight="600" color="#1a1a1a">
                Payslip
              </Typography>
            </Box>
            <Chip 
              label={`${displayMonth} ${displayYear}`}
              size="small"
              color={isCurrentMonth ? "primary" : "default"}
              variant={isCurrentMonth ? "filled" : "outlined"}
              sx={{ borderRadius: '8px', fontSize: '12px', height: '24px' }}
            />
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Info banner for non-current month */}
          {!isCurrentMonth && (
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: '#fef3e2', p: 0.75, borderRadius: 1.5 }}>
              <Info sx={{ fontSize: 13, color: '#f57c00' }} />
              <Typography variant="caption" color="#e65100" sx={{ fontSize: '11px', lineHeight: 1.2 }}>
                Showing latest available payslip from {displayMonth} {displayYear}
              </Typography>
            </Box>
          )}

          {/* Salary Stats Row */}
          <Box display="flex" gap={1} mb={2}>
            <SalaryBox>
              <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={0.5}>
                <TrendingUp sx={{ fontSize: 10, color: '#1565c0' }} />
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '9px' }}>
                  Gross Salary
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: '14px', color: '#1565c0' }}>
                {formatCurrency(payslip?.gross_salary)}
              </Typography>
            </SalaryBox>
            
            <SalaryBox>
              <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={0.5}>
                <TrendingDown sx={{ fontSize: 10, color: '#c62828' }} />
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '9px' }}>
                  Deductions
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: '14px', color: '#c62828' }}>
                {formatCurrency(payslip?.total_deductions)}
              </Typography>
            </SalaryBox>
            
            <SalaryBox >
              <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={0.5}>
                <AccountBalanceWallet sx={{ fontSize: 10, color: '#2e7d32' }} />
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '9px' }}>
                  Net Salary
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="700" sx={{ fontSize: '14px', color: '#2e7d32' }}>
                {formatCurrency(payslip?.net_salary)}
              </Typography>
            </SalaryBox>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={1.5}>
            <ActionButton
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={18} /> : <Download sx={{ fontSize: 18 }} />}
              onClick={handleDownloadPDF}
              disabled={downloading}
              // sx={{
              //   background: 'linear-gradient(135deg, #8a5cf6a4 0%, #7c3aed8d 100%)',
              //   '&:hover': {
              //     background: 'linear-gradient(135deg, #7c3aed94 0%, #6c28d9a1 100%)',
              //   }
              // }}
            >
              {downloading ? 'Downloading...' : 'Download'}
            </ActionButton>
            <ActionButton
              variant="outlined"
              startIcon={<Visibility sx={{ fontSize: 18 }} />}
              onClick={handleViewFull}
              sx={{ borderColor: '#e0e0e0', color: '#666' }}
            >
              View Full
            </ActionButton>
          </Box>

          {/* Optional: Show additional info */}
          <Box mt={2} pt={1}>
            {/* <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CurrencyRupee sx={{ fontSize: 12 }} />
              All amounts are in Indian Rupees (₹)
            </Typography> */}
          </Box>
        </CardContent>
      </StyledCard>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EmployeePayslipCard;